import { useState } from 'react';
import { JamPadEvent } from '../DataModels';
import { Box, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PersonOutline, Place, Schedule } from '@mui/icons-material';
import EventActions from './EventActions.tsx';
import { useNavigate } from 'react-router-dom';

interface Props { event: JamPadEvent | undefined, col: boolean }
export const EventItem = ({ event, col } : Props) => {
    const nav = useNavigate();
    const [shown, setShown] = useState(true);

    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down('md'));

    return (shown && 
        <Box sx={{
            display: 'flex', flexDirection: { xs: 'column', md: col ? 'column' : 'row' },
            justifyContent: 'center', gap: '20px', alignItems: 'center',
            textAlign: 'center',  backgroundColor: "black", outline: "4px solid grey", padding: "10px",
        }}>
            {/* Image Banner */}
            <Box sx={{ 
                flex: {xs: '1 0 60%', md: '0 1 45%' },
                maxHeight: { xs: '70vh', md: '80vh' }, 
                display: 'flex', padding: "10px", 
            }}> 
                <img src={event?.poster ?? undefined} alt={event?.title}
                    style={{ 
                        flex: '1', maxWidth: '100%', objectFit: 'contain', margin: '0 auto',
                        borderRadius: 2, outline: "2px solid black", 
                    }} />
            </Box>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', 
                gap: '14px', padding: '10px 20px 24px', flex: '1'
            }}>
                { event?.title &&
                    <Typography variant='h4' fontWeight='bold' style={{ padding: "0", color: 'white' }}>
                        {event?.title}
                    </Typography>
                }

                <Box sx={{ 
                    display: 'flex', padding: '0 20px', color: 'lightgray', 
                    gap: '0', flexDirection: 'column',
                    justifyContent: 'center', alignItems: 'center'
                }}>
                    <Typography variant='h6' style={{ fontSize: '24px' }}>
                        { event?.date.toLocaleString('en-US', {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'}) }
                    </Typography>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Schedule sx={{ fontSize: '28px' }}/>
                        <Typography variant='h6' style={{ fontSize: '24px' }}>
                            { event?.date.toLocaleTimeString('en-US', {minute: '2-digit', hour: 'numeric'})}
                            
                            { event?.end_date &&
                                " - " + (new Date(event.end_date)).toLocaleTimeString('en-US', {minute: '2-digit', hour: 'numeric'})
                            }
                        </Typography>
                    </div>
                </Box>

                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'pink', gap: '8px' }}>
                        <Place sx={{ fontSize: '24px' }}/>
                        <Typography fontWeight='bold' sx={{ color: 'white' }} variant='h5'>
                            {event?.name}
                        </Typography>
                    </div>
                    <Typography sx={{ flex: '1 0 100%', color: 'lightblue' }} variant='body1'>
                        <Link color="inherit" target="_blank" href={"https://www.google.com/maps/search/?api=1&query=" + event?.address}>
                            <i>{event?.address}</i>
                        </Link>
                    </Typography>
                </div>
                    
                <Box sx={{ 
                    display: 'flex', flexWrap: 'wrap', gap: '12px', 
                    margin: '16px 4%',
                    outline: "2px solid grey", padding: "20px 10px", borderRadius: '14px', justifyContent: 'center'
                }}>
                    <Typography variant='body1' style={{ background: 'white', padding: "14px", borderRadius: '18px' }}> 
                        { event?.price !== 0 ? "$ " + event?.price : "FREE" }
                    </Typography>
                    <Typography component='div' variant='body1' style={{ 
                        background: "white", padding: "14px", borderRadius: '18px',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                        <PersonOutline/>
                        <Typography>{ event?.age === 0 ? "All Ages" : event?.age + "+" }</Typography>
                    </Typography>
                    <Typography component='div' variant='body1' style={{ 
                        background: "white", padding: "14px", borderRadius: '18px',
                        display: 'flex', alignItems: 'center', gap: '6px'
                    }}>
                        <Typography>{event?.rsvps ?? -1}</Typography>
                        <Typography variant='caption'>{ (event?.rsvps ?? 0) != 1 ? "RSVPs" : "RSVP" }</Typography>
                    </Typography>
                    {(event?.tags?.length ?? 0 > 0) &&
                        <Typography style={{ flex: '1 0 100%' }}></Typography> 
                    }
                    {(event?.tags?.length ?? 0 > 0) &&
                        event?.tags.map((t) => {
                            return <Typography variant='body2' style={{ 
                                fontWeight: 'bold', fontVariantCaps: 'small-caps',
                                color: 'darkblue', background: 'lightpink', padding: "10px", borderRadius: '10px',
                            }}>{t.toUpperCase()}</Typography>
                        })
                    }
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', 
                    gap: '10px', justifyContent: 'center', alignItems: 'center',
                    margin: '0 4%', 
                    paddingBottom: '20px'
                }}>
                    <Typography variant='h6' sx={{ color: 'white', fontWeight: 'bold', }}>
                        { event?.artist_names.length === 0 ? "[ No featured creators ]" : "FEATURING:" }
                    </Typography>

                    { event?.artist_names.map((name, i) => {
                        return <Typography variant='body1' fontWeight='bold' 
                        onClick={(e) => {
                            e.stopPropagation();
                            if(event.artist_ids[i] && window.confirm("Go to @" + name + "'s profile?")) {
                                nav('/profile/' + name);
                                setShown(false);
                            }
                        }} style={{ 
                            border: '2px solid white', textDecoration: 'none',
                            background: event.artist_ids[i] !== null ? 'darkblue' : 'lightblue', 
                            color: event.artist_ids[i] !== null ? 'white' : 'black',
                            padding: '10px 14px', borderRadius: '12px'
                        }}>{name}</Typography>
                    })}
                </Box>

                <EventActions event={event}/>
            </div>
        </Box> 
    );
};
export default EventItem;